import { useState } from "react"
import { useOutletContext } from "react-router-dom"
import ToolShell from "../components/ToolShell"
import CTABox from "../components/CTABox"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

export default function PropertyManagementFeeCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [monthlyRent, setMonthlyRent] = useState(1500)
  const [mgmtFee, setMgmtFee] = useState(10)
  const [leasingFee, setLeasingFee] = useState(1500)
  const [tenancyLength, setTenancyLength] = useState(18)
  const [maintMarkup, setMaintMarkup] = useState(10)
  const [annualMaintenance, setAnnualMaintenance] = useState(1500)
  const [hourlyValue, setHourlyValue] = useState(25)
  const [hoursPerMonth, setHoursPerMonth] = useState(5)

  // Calcs
  const annualMgmtFees = monthlyRent * 12 * (mgmtFee / 100)
  const annualizedLeasingFee = tenancyLength > 0 ? leasingFee / (tenancyLength / 12) : 0
  const annualMaintMarkup = annualMaintenance * (maintMarkup / 100)
  const totalPMCost = annualMgmtFees + annualizedLeasingFee + annualMaintMarkup
  const pmCostAsPctOfRent = (totalPMCost / (monthlyRent * 12)) * 100

  const selfMgmtTimeCost = hoursPerMonth * 12 * hourlyValue
  const netCostOfPM = totalPMCost - selfMgmtTimeCost

  return (
    <ToolShell>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 sm:p-8">
        
        {/* Input Column */}
        <div className="space-y-6">
          <div className="grid border border-slate-200 p-5 rounded-xl bg-slate-50 gap-4">
            <h4 className="font-semibold text-slate-800">Hard Costs (Property Manager)</h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={monthlyRent || ''} onChange={(e) => setMonthlyRent(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Management Fee: {mgmtFee}%</label>
              <input type="range" min="0" max="25" step="0.5" value={mgmtFee} onChange={(e) => setMgmtFee(Number(e.target.value))} className="w-full accent-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Leasing Fee (per tenant)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                  <input type="number" value={leasingFee || ''} onChange={(e) => setLeasingFee(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Avg. Tenancy (months)</label>
                <input type="number" value={tenancyLength || ''} onChange={(e) => setTenancyLength(Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Maintenance Markup: {maintMarkup}%</label>
              <input type="range" min="0" max="30" step="1" value={maintMarkup} onChange={(e) => setMaintMarkup(Number(e.target.value))} className="w-full accent-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Estimated Annual Maintenance Bills</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}</span>
                <input type="number" value={annualMaintenance || ''} onChange={(e) => setAnnualMaintenance(Number(e.target.value))} className="w-full pl-8 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
          </div>

          <div className="grid border border-slate-200 p-5 rounded-xl bg-slate-50 gap-4">
            <h4 className="font-semibold text-slate-800">Soft Costs (Self-Managing)</h4>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Your Personal Hourly Value</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-slate-400">{currency}/hr</span>
                <input type="number" value={hourlyValue || ''} onChange={(e) => setHourlyValue(Number(e.target.value))} className="w-full pl-12 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Hours Spent Self-Managing (Monthly)</label>
              <input type="number" value={hoursPerMonth || ''} onChange={(e) => setHoursPerMonth(Number(e.target.value))} className="w-full px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-primary focus:border-primary bg-white" />
            </div>
          </div>
        </div>

        {/* Results Column */}
        <div className="space-y-6">
          <div className="bg-white border shadow-sm border-slate-200 p-6 sm:p-8 rounded-2xl flex flex-col">
            <span className="text-sm font-medium text-slate-500 mb-1">Total True Cost of PM (Annual)</span>
            <span className="text-5xl font-extrabold text-slate-900 tracking-tight">{currency}{totalPMCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            <span className="text-slate-500 mt-2">Which equates to <strong className="text-slate-800">{pmCostAsPctOfRent.toFixed(1)}%</strong> of gross rent</span>
            
            <div className={`mt-6 p-4 rounded-xl border ${netCostOfPM < 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
              <h4 className="font-bold flex items-center gap-2">Verdict:</h4>
              <p className="mt-1 text-sm leading-relaxed">
                {netCostOfPM < 0 
                  ? "Property management pays for itself based on your time value. Self-managing would cost you more in time than you save in fees."
                  : `You'd save roughly ${currency}${Math.floor(netCostOfPM).toLocaleString()} a year by self-managing, assuming you have the ${hoursPerMonth} hours/month to spare.`}
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <h4 className="font-semibold text-slate-900 mb-4">Cost vs Cost Breakdown</h4>
            <div className="space-y-4 text-sm">
              <div>
                <div className="flex justify-between mb-1 text-slate-500"><span>Base Mgmt Fees</span> <span>{currency}{Math.round(annualMgmtFees)}</span></div>
                <div className="flex justify-between mb-1 text-slate-500"><span>Annualized Leasing</span> <span>{currency}{Math.round(annualizedLeasingFee)}</span></div>
                <div className="flex justify-between font-medium text-slate-900 pt-2 border-t border-slate-200"><span>Hard Cost of PM</span><span>{currency}{Math.round(totalPMCost)}</span></div>
              </div>
              
              <div className="pt-4 border-t border-slate-200">
                <div className="flex justify-between mb-1 text-slate-500"><span>Personal Time Valuation</span> <span>{currency}{Math.round(selfMgmtTimeCost)}</span></div>
                <div className="flex justify-between font-medium text-slate-900 pt-2 border-t border-slate-200"><span>Soft Cost of Self Mgmt</span><span>{currency}{Math.round(selfMgmtTimeCost)}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 sm:px-8 pb-10">
        <FormulaBox 
          formula={`Annual Mgmt Fee = Rent × 12 × Mgmt Pct\nAnnualized Leasing = Leasing Fee / (Tenancy Length / 12)\nMaint Markup Cost = Annual Maintenance × Markup Pct\n\nTotal PM Cost = Annual Mgmt Fee + Annualized Leasing + Maint Markup Cost\n\nSelf-Mgmt Soft Cost = Hours Per Month × 12 × Hourly Value`} 
        />
        
        <CTABox 
          icon={tool.icon} 
          heading="Want to self-manage with zero friction?" 
          body={tool.ctaText} 
          ctaLabel="Try RentKeep Free" 
          ctaHref="/register" 
        />
        
        <ToolFAQ 
          items={[
            { q: "Why include maintenance markups?", a: "Many property management companies add a 10% to 20% surcharge on contractor invoices to cover coordination. It's a hidden cost that adds up fast over a year." },
            { q: "Why annualized leasing?", a: "If you pay a {currency}1,500 finders fee, but the tenant stays for 3 years, the true cost of that fee is {currency}500/year. Amortizing it against average tenancy length provides an accurate annual estimate." }
          ]} 
        />
        
        <RelatedToolsList relatedSlugs={tool.related} />
      </div>
    </ToolShell>
  )
}
